import {isDefined, isPopulated} from './typeGuards';
import {IconMail, IconPhone} from 'Images/icons';

import type {LinkField} from 'types/payload-types';

/**
 * Returns a specific icon component based on the provided link type.
 * This utility maps a given `linkType` (such as `'phone'` or `'email'`) to its corresponding icon component.
 * If the `linkType` does not match a known case, it returns `null`, indicating that no icon should be rendered.
 * This is typically used to dynamically render icons in UI components based on the type of link selected by the user.
 *
 * @param linkType The type of the link for which an icon should be returned. Supported values: `'phone'`, `'email'`.
 * @returns The corresponding icon component (`IconPhone` or `IconMail`), or `null` if no match is found.
 *
 * @example
 * ```tsx
 * const Icon = getIconForLinkType(linkType);
 * return Icon ? <Icon /> : null;
 * ```
 */
export const getIconForLinkType = (linkType: LinkField['linkType']) => {
    if (linkType === 'phone') {
        return IconPhone;
    }
    if (linkType === 'email') {
        return IconMail;
    }

    return null;
};

/**
 * Returns the appropriate `href` value for a given link object based on its `linkType`.
 * This utility function evaluates the type of link and builds the correct `href` string accordingly.
 * It supports link types such as phone (`tel:`), email (`mailto:` with subject and body), external URLs, internal pages,
 * anchor links within pages, and file downloads. If no valid destination is found, a fallback such as `'/'` or `'#'` is returned.
 * This function ensures consistent URL generation for all link types defined in the `LinkField` schema.
 *
 * @param link The link object containing type and related metadata to resolve the target URL.
 * @returns A string representing the resolved `href` value for use in anchors or buttons.
 *
 * @example
 * ```tsx
 * const href = getHrefForLinkType(link);
 * return <a href={href}>{link.label}</a>;
 * ```
 */
// eslint-disable-next-line complexity
export const getHrefForLinkType = (link: LinkField) => {
    if (link.linkType === 'phone') {
        return `tel: ${link.phone}`;
    }
    if (link.linkType === 'email') {
        return `mailto: ${link.email}?subject=${link.emailSubject ?? ''}&body=${link.emailBody ?? ''}`;
    }
    if (link.linkType === 'external') {
        return link.externalUrl ?? '';
    }
    if (link.linkType === 'internal') {
        if (isDefined(link.internalLink) && isPopulated(link.internalLink.value)) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            return link.internalLink.value.slug ?? '/';
        }

        return '/';
    }
    if (link.linkType === 'anchor') {
        if (isDefined(link.internalBlockLink) && isPopulated(link.internalBlockLink.value)) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            return `${link.internalBlockLink.value?.slug ?? ''}${link?.blockId ?? ''}`;
        }

        return '/';
    }

    if (isDefined(link.file) && isPopulated(link.file)) {
        return link.file.url ?? '';
    }

    return '#';
};

/**
 * Determines the appropriate value for the `download` attribute of an anchor tag based on the link configuration.
 * This function checks if the link type is `'file'` and whether the `directDownload` option is enabled.
 * If so, it returns either a custom filename (if provided) or `true` to trigger a default download.
 * Otherwise, it returns `false`, indicating that the `download` attribute should not be set.
 *
 * @param link The link object containing type, download settings, and optional filename.
 * @returns A string with the custom filename, `true` for default behavior, or `false` if downloading is not applicable.
 *
 * @example
 * ```tsx
 * const download = getDownloadAttribute(link);
 * <a href={href} download={download}>{link.label}</a>;
 * ```
 */
export const getDownloadAttribute = (link: LinkField) => {
    if (link.linkType === 'file' && link.directDownload) {
        return link.downloadFilename ?? true;
    }

    return false;
};

/**
 * Generates the `rel` attribute string for an anchor tag based on the given external link configuration.
 * This function ensures that security and SEO best practices are followed when rendering external links.
 * For external links, it always includes `"noopener noreferrer"` to prevent tab-napping vulnerabilities.
 * If the `rel` checkbox is **not** checked (meaning "do not follow"), it appends `"nofollow"` to discourage search engine indexing.
 * For all non-external link types, it returns `undefined`, allowing the anchor to omit the `rel` attribute entirely.
 *
 * @param link The link object from which to determine the appropriate `rel` attribute.
 * @returns A string containing the `rel` attribute value, or `undefined` if not applicable.
 *
 * @example
 * ```tsx
 * const rel = getRelAttributes(link);
 * <a href={href} rel={rel}>{link.label}</a>;
 * ```
 */
export const getRelAttributes = (link: LinkField) => {
    if (link.linkType === 'external') {
        return `noopener noreferrer${link.rel ? '' : ' nofollow'}` as const;
    }

    return undefined;
};

/**
 * Determines the appropriate `target` attribute value for a given link based on its type.
 * This utility inspects the `linkType` property of the provided `link` object. If the link is of type
 * `'external'`, it returns `'_blank'`, which instructs the browser to open the link in a new tab or window.
 * For all other link types, it returns `undefined`, meaning the link will follow the browser's default
 * behavior (typically opening in the same tab).
 *
 * @param link The link object containing information about its type and other metadata.
 * @returns `'_blank'` if the link is external, otherwise `undefined`.
 *
 * @example
 * ```tsx
 * const target = getTargetAttribute({ linkType: 'external', url: 'https://example.com' });
 * // target === '_blank'
 * ```
 */
export const getTargetAttribute = (link: LinkField) => {
    if (link.linkType === 'external' || link.linkType === 'file') {
        return '_blank' as const;
    }

    return undefined;
};